import React from "react";
import { useSelector } from "react-redux";
import { Box, Typography } from "@mui/material";

import { RootState } from "../../../../store";
import { scaleFont } from "../LabPrintFunctions";
import { getSpeciality } from "../../../../reducers/serviceTags";
import { spFullNameSelector } from "../../../../reducers/serviceProvider";
import { ServiceProvider } from "../../../../interfaces/ServiceProvidersInterface";
import { useShowSignatureInLabPrint } from "../../../../hooks/useShowSignatureInLabPrint";

export interface AuthorizedSp {
  id: number;
  signature: string;
  designation: string;
  name: string;
  fullname?: string;
  qualification: string;
  registrationNumber: string;
  specialities: string;
  role: string;
  speciality: string;
  title: string;
  userGroup: string;
}

interface AuthorizedSpInfoProps {
  rootStyles?: React.CSSProperties;
  imageStyles?: React.CSSProperties;
  sp: AuthorizedSp;
}

// If logged in, use the service provider from the state
// If not use the sp as sp is inserted inside the labtest

const AuthorizedSpInfo: React.FC<AuthorizedSpInfoProps> = ({
  sp,
  rootStyles = {},
  imageStyles = {}
}) => {
  const { showSignatureInLabPrint } = useShowSignatureInLabPrint();
  const authenticated = useSelector((state: RootState) => state.authenticated);
  const serviceTags = useSelector((state: RootState) => state.bookableResources.serviceTags);
  const serviceProviders = useSelector(
    (state: RootState) => state.resources?.resourceCentreServiceProviders
  );

  const serviceProvider = serviceProviders?.find((s: ServiceProvider) => s.id === sp.id);

  const signature = serviceProvider?.signature ?? sp?.signature ?? "";
  const designation = serviceProvider?.designation ?? sp?.designation ?? "";
  const qualification = serviceProvider?.qualification ?? sp?.qualification ?? "";
  const registrationNumber = serviceProvider?.registrationNumber ?? sp?.registrationNumber ?? "";
  const name =
    (serviceProvider && spFullNameSelector(serviceProvider)) ??
    `${sp?.title ?? ""} ${sp?.name ?? sp?.fullname ?? ""}`.trim();
  const speciality =
    (serviceProvider && getSpeciality(serviceTags, serviceProvider.specialities?.[0])) ??
    sp?.specialities ??
    sp?.speciality ??
    "";

  const shouldShowSignature = authenticated ? showSignatureInLabPrint : Boolean(signature);

  return (
    <Box style={rootStyles}>
      <Box style={{ width: "6cm" }}>
        <Box
          component="div"
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          marginTop="0.2cm"
          minHeight="2cm"
        >
          {shouldShowSignature && signature && (
            <img src={signature} style={{ ...imageStyles, height: "1.8cm" }} alt="signature" />
          )}
        </Box>

        <Typography
          data-testmation="authorizedSp"
          style={{
            fontSize: scaleFont("0.33cm", 0.95),
            width: "6cm",
            textAlign: "left",
            borderTop: "1px solid lightgrey",
            fontWeight: 600,
            marginTop: "6px",
            textTransform: "capitalize",
            paddingTop: "0.18cm"
          }}
        >
          {name}
        </Typography>
      </Box>

      {designation ? (
        <Typography sx={{ whiteSpace: "pre-wrap" }}>{designation}</Typography>
      ) : (
        <div>
          <Box display="flex" flexDirection="column">
            <Typography>
              <Typography>{qualification}</Typography>

              {speciality}

              {registrationNumber && <Typography>{registrationNumber}</Typography>}
            </Typography>
          </Box>
        </div>
      )}
    </Box>
  );
};

export default AuthorizedSpInfo;
